import { Annotation, Prec, Transaction } from "@codemirror/state";
import { EditorView, ViewPlugin } from "@codemirror/view";
import { EventEmitter } from "events";

import { InsertOp, RemoveOp, RetainOp, TextOperation } from "components/organisms/editor/core/model";
import RangesTracker from "components/organisms/editor/core/ranges";
import { rangesUpdatedEffect, setTrackChangesUserId } from "components/organisms/editor/history/historyOt";
import { trackedDeletesFromState } from "components/organisms/editor/history/trackedDeletes";
import { debugConsole } from "components/organisms/editor/collaboration/sharejs/vendor/support";

export const trackChangesAnnotation = Annotation.define();

function chooseOrigin(transaction) {
    if (transaction.annotation(Transaction.remote)) {
        return "remote";
    }
    if (transaction.annotation(Transaction.userEvent) === "undo") {
        return "undo";
    }
    if (transaction.annotation(trackChangesAnnotation) === "reject") {
        return "reject";
    }
    return undefined;
}

function summarizeTransaction(transaction) {
    return {
        docChanged: !!transaction?.docChanged,
        origin: chooseOrigin(transaction),
        newLength: transaction?.changes?.newLength ?? null,
        empty: transaction?.changes?.empty ?? null,
        userEvent: transaction?.annotation?.(Transaction.userEvent) ?? null,
        remote: transaction?.annotation?.(Transaction.remote) ?? null,
    };
}

export const realtime = ({ currentDoc }, handleError) => {
    const realtimePlugin = ViewPlugin.define((view) => {
        const editor = new EditorFacade(view);
        currentDoc.attachToCM6(editor);

        return {
            update(update) {
                if (update.docChanged) {
                    editor.handleUpdateFromCM(update.transactions, currentDoc.ranges);
                }
            },
            destroy() {
                currentDoc.detachFromCM6(editor);
            },
        };
    });

    const ensureRealtimePlugin = EditorView.updateListener.of((update) => {
        if (!update.view.plugin(realtimePlugin)) {
            const message = "The realtime extension has been destroyed!!";
            debugConsole.warn(message);
            if (currentDoc.doc) {
                currentDoc.doc.emit("error", message);
            } else {
                handleError(new Error(message));
            }
        }
    });

    return Prec.highest([realtimePlugin, ensureRealtimePlugin]);
};

export class EditorFacade extends EventEmitter {
    constructor(view) {
        super();
        this.view = view;
        this.otAdapter = null;
        this.events = new EventEmitter();
    }

    getValue() {
        return this.view.state.doc.toString();
    }

    cmChange(changes, origin) {
        const isRemote = origin === "remote";

        this.view.dispatch({
            changes,
            annotations: [Transaction.remote.of(isRemote), Transaction.addToHistory.of(!isRemote)],
            effects: isRemote ? this.view.scrollSnapshot().map(this.view.state.changes(changes)) : undefined,
        });
    }

    cmInsert(position, text, origin) {
        this.cmChange({ from: position, insert: text }, origin);
    }

    cmDelete(position, text, origin) {
        this.cmChange({ from: position, to: position + text.length }, origin);
    }

    attachShareJs(shareDoc, maxDocLength) {
        this.otAdapter =
            shareDoc.otType === "history-ot"
                ? new HistoryOTAdapter(this, shareDoc, maxDocLength)
                : new ShareLatexOTAdapter(this, shareDoc, maxDocLength);
        this.otAdapter.attachShareJs();
    }

    attachShareDoc(shareDoc, maxDocLength) {
        this.attachShareJs(shareDoc, maxDocLength);
    }

    detachShareJs() {
        this.otAdapter = null;
    }

    detachShareDoc() {
        this.detachShareJs();
    }

    handleUpdateFromCM(transactions, ranges) {
        if (this.otAdapter == null) {
            throw new Error("Trying to process updates with no otAdapter");
        }
        this.otAdapter.handleUpdateFromCM(transactions, ranges);
    }

    setTrackChangesUserId(userId) {
        if (this.otAdapter instanceof HistoryOTAdapter) {
            this.view.dispatch(setTrackChangesUserId(userId));
        }
    }
}

class ShareLatexOTAdapter {
    constructor(editor, shareDoc, maxDocLength) {
        this.editor = editor;
        this.shareDoc = shareDoc;
        this.maxDocLength = maxDocLength;
    }

    attachShareJs() {
        const shareDoc = this.shareDoc;
        const check = () => {
            window.setTimeout(() => {
                const editorText = this.editor.getValue();
                const otText = shareDoc.getText();

                if (editorText !== otText) {
                    this.shareDoc.emit("error", "Text does not match in CodeMirror 6");
                    debugConsole.error("Text does not match!");
                    debugConsole.error(`editor: ${editorText}`);
                    debugConsole.error(`ot:     ${otText}`);
                }
            }, 0);
        };

        const onInsert = (pos, text) => {
            this.editor.cmInsert(pos, text, "remote");
            check();
        };

        const onDelete = (pos, text) => {
            this.editor.cmDelete(pos, text, "remote");
            check();
        };

        check();

        shareDoc.on("insert", onInsert);
        shareDoc.on("delete", onDelete);

        shareDoc.detach_cm6 = () => {
            shareDoc.removeListener("insert", onInsert);
            shareDoc.removeListener("delete", onDelete);
            delete shareDoc.detach_cm6;
            this.editor.detachShareJs();
        };
    }

    handleUpdateFromCM(transactions, ranges) {
        const shareDoc = this.shareDoc;
        const trackedDeletesLength = ranges?.getTrackedDeletesLength?.() || 0;

        for (const transaction of transactions) {
            if (!transaction.docChanged) {
                continue;
            }

            const origin = chooseOrigin(transaction);
            if (origin === "remote") {
                return;
            }

            const fullDocLength = transaction.changes.desc.newLength + trackedDeletesLength;
            if (this.maxDocLength && fullDocLength >= this.maxDocLength) {
                shareDoc.emit("error", new Error("document length is greater than maxDocLength"));
                return;
            }

            let positionShift = 0;
            transaction.changes.iterChanges((fromA, toA, fromB, toB, insertedText) => {
                const fromUndo = origin === "undo" || origin === "reject";
                const insertedLength = insertedText.length;
                const removedLength = toA - fromA;
                const inserted = insertedLength > 0;
                const removed = removedLength > 0;
                const pos = fromA + positionShift;

                if (removed) {
                    shareDoc.del(pos, removedLength, fromUndo);
                }

                if (inserted) {
                    shareDoc.insert(pos, insertedText.toString(), fromUndo);
                }

                positionShift = positionShift - removedLength + insertedLength;
                this.editor.emit("change", this.editor, {
                    origin,
                    inserted,
                    removed,
                });
            });
        }
    }
}

class HistoryOTAdapter {
    constructor(editor, shareDoc, maxDocLength) {
        this.editor = editor;
        this.shareDoc = shareDoc;
        this.maxDocLength = maxDocLength;
    }

    attachShareJs() {
        this.checkContent();

        const onRemoteOp = this.onRemoteOp.bind(this);
        this.shareDoc.on("remoteop", onRemoteOp);

        this.shareDoc.detach_cm6 = () => {
            this.shareDoc.removeListener("remoteop", onRemoteOp);
            delete this.shareDoc.detach_cm6;
            this.editor.detachShareJs();
        };
    }

    handleUpdateFromCM(transactions) {
        for (const transaction of transactions) {
            if (this.maxDocLength && transaction.changes.newLength >= this.maxDocLength) {
                this.shareDoc.emit("error", new Error("document length is greater than maxDocLength"));
                return;
            }

            const origin = chooseOrigin(transaction);
            transaction.changes.iterChanges((fromA, toA, fromB, toB, insertedText) => {
                this.onCodeMirrorChange(fromA, toA, fromB, toB, insertedText, origin);
            });
        }
    }

    onRemoteOp(operations) {
        const trackedDeletes = trackedDeletesFromState(this.editor.view.state);

        for (const operation of operations) {
            const changes = [];
            let trackedChangesUpdated = false;

            if (operation instanceof TextOperation) {
                let cursor = 0;
                for (const op of operation.ops) {
                    if (op instanceof InsertOp) {
                        if (op.tracking?.type !== "delete") {
                            changes.push({
                                from: trackedDeletes.toCodeMirror(cursor),
                                insert: op.insertion,
                            });
                        }
                        trackedChangesUpdated = true;
                    } else if (op instanceof RemoveOp) {
                        changes.push({
                            from: trackedDeletes.toCodeMirror(cursor),
                            to: trackedDeletes.toCodeMirror(cursor + op.length),
                        });
                        cursor += op.length;
                        trackedChangesUpdated = true;
                    } else if (op instanceof RetainOp) {
                        if (op.tracking != null) {
                            if (op.tracking.type === "delete") {
                                changes.push({
                                    from: trackedDeletes.toCodeMirror(cursor),
                                    to: trackedDeletes.toCodeMirror(cursor + op.length),
                                });
                            }
                            trackedChangesUpdated = true;
                        }
                        cursor += op.length;
                    }
                }
            }

            const view = this.editor.view;
            const effects = [];
            const scrollEffect = view.scrollSnapshot().map(view.state.changes(changes));
            if (scrollEffect != null) {
                effects.push(scrollEffect);
            }
            if (trackedChangesUpdated) {
                effects.push(rangesUpdatedEffect.of(null));
            }

            view.dispatch({
                changes,
                effects,
                annotations: [Transaction.remote.of(true), Transaction.addToHistory.of(false)],
            });
        }
    }

    onCodeMirrorChange(fromA, toA, fromB, toB, insertedText, origin) {
        const insertedLength = insertedText.length;
        const removedLength = toA - fromA;
        const inserted = insertedLength > 0;
        const removed = removedLength > 0;

        this.editor.emit("change", this.editor, {
            origin,
            inserted,
            removed,
        });
    }

    checkContent() {
        window.setTimeout(() => {
            const editorText = this.editor.getValue();
            const otText = this.shareDoc.getText();

            if (editorText !== otText) {
                this.shareDoc.emit("error", "Text does not match in CodeMirror 6");
                debugConsole.error("Text does not match!");
                debugConsole.error(`editor: ${editorText}`);
                debugConsole.error(`ot:     ${otText}`);
            }
        }, 0);
    }
}

export default realtime;
